import { createContext, useContext } from 'react';

/** `system` follows the OS preference and tracks its changes live. */
export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'novelist_theme_mode';

export const readStoredThemeMode = (): ThemeMode => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' || value === 'system'
      ? value
      : 'system';
  } catch {
    // Storage can be unavailable (private mode); default per session.
    return 'system';
  }
};

export const storeThemeMode = (mode: ThemeMode): void => {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // The choice then lives only as long as the tab.
  }
};

export const systemPrefersDark = (): boolean =>
  window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;

interface ThemeModeContextValue {
  mode: ThemeMode;
  /** The resolved appearance: `system` collapsed to what the OS says. */
  isDark: boolean;
  setMode: (mode: ThemeMode) => void;
}

export const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: 'system',
  isDark: false,
  setMode: () => undefined,
});

export const useThemeMode = () => useContext(ThemeModeContext);
